import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Suspense, lazy, useEffect } from "react";
import { checkRedirectResult } from "@/lib/firebaseAuth";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";

// Import loading spinner
import { LoadingSpinner } from "@/components/ui/loading-spinner";

// Lazy load all page components
const Index = lazy(() => import("./pages/Index"));
const NotFound = lazy(() => import("./pages/NotFound"));
const RunwayCalculator = lazy(() => import("./pages/RunwayCalculator"));
const PLReader = lazy(() => import("./pages/PLReader"));
const Pricing = lazy(() => import("./pages/Pricing"));
const About = lazy(() => import("./pages/About"));
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Settings = lazy(() => import("./pages/Settings"));
const FounderPulseCheck = lazy(() => import("./pages/tools/FounderPulseCheck") as Promise<{ default: React.ComponentType<object> }>);
const PlainSpeakCFO = lazy(() => import("./pages/tools/PlainSpeakCFO") as Promise<{ default: React.ComponentType<object> }>);
const PricingSimulator = lazy(() => import("./pages/tools/PricingSimulator"));
const BurnRateOptimizer = lazy(() => import("./pages/tools/BurnRateOptimizer"));
const GTMPlanGenerator = lazy(() => import("./pages/tools/GTMPlanGenerator"));
const CampaignBuilder = lazy(() => import("./pages/tools/CampaignBuilder") as Promise<{ default: React.ComponentType<object> }>);
const CustomerInterviewKit = lazy(() => import("./pages/tools/CustomerInterviewKit"));
const TimeAuditMapper = lazy(() => import("./pages/tools/TimeAuditMapper"));
const MetricsDashboardDesigner = lazy(() => import("./pages/tools/MetricsDashboardDesigner"));
const HiringPlanGenerator = lazy(() => import("./pages/tools/HiringPlanGenerator") as Promise<{ default: React.ComponentType<object> }>);
const InvestorUpdateBuilder = lazy(() => import("./pages/tools/InvestorUpdateBuilder"));

const queryClient = new QueryClient();

// Check for redirect results from OAuth sign-in
function AuthRedirectHandler() {
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    async function handleRedirect() {
      try {
        const result = await checkRedirectResult();
        if (result) {
          toast({
            title: "Signed in successfully!",
            description: "Welcome to VentureVault",
            variant: "default",
          });
          navigate("/dashboard");
        }
      } catch (error) {
        console.error("Error handling redirect:", error);
        toast({
          title: "Sign in failed",
          description: "There was a problem signing in with Google",
          variant: "destructive",
        });
      }
    }

    handleRedirect();
  }, [toast, navigate]);

  return null;
}

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthRedirectHandler />
          <Suspense fallback={<LoadingSpinner fullScreen size="lg" text="Loading application..." />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/tools/runway-calculator" element={<RunwayCalculator />} />
              <Route path="/tools/pl-reader" element={<PLReader />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/about" element={<About />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/dashboard/settings" element={<Settings />} />
              <Route path="/dashboard/tools/founder-pulse-check" element={<FounderPulseCheck />} />
              <Route path="/dashboard/tools/plainspeak-cfo" element={<PlainSpeakCFO />} />
              <Route path="/dashboard/tools/pricing-simulator" element={<PricingSimulator />} />
              <Route path="/dashboard/tools/burn-rate-optimizer" element={<BurnRateOptimizer />} />
              <Route path="/dashboard/tools/gtm-plan-generator" element={<GTMPlanGenerator />} />
              <Route path="/dashboard/tools/campaign-builder" element={<CampaignBuilder />} />
              <Route path="/dashboard/tools/interview-kit" element={<CustomerInterviewKit />} />
              <Route path="/dashboard/tools/time-audit" element={<TimeAuditMapper />} />
              <Route path="/dashboard/tools/metrics-designer" element={<MetricsDashboardDesigner />} />
              <Route path="/dashboard/tools/hiring-plan" element={<HiringPlanGenerator />} />
              <Route path="/dashboard/tools/investor-updates" element={<InvestorUpdateBuilder />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
